import { useMemo, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ArrowLeft,
  Calendar,
  User,
  Tag,
  Newspaper,
  Share2,
  ArrowRight,
} from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { formatDate } from '../../utils/helpers'

const categoryLabels = {
  devotional: 'Devotional',
  'sermon-recap': 'Sermon Recap',
  'church-news': 'Church News',
  testimony: 'Testimony',
  teaching: 'Teaching',
}

const BlogPost = () => {
  const { slug } = useParams()
  const { data: posts, loading } = useCollection('blogPosts', {
    orderByField: 'publishedAt',
    orderDirection: 'desc',
  })

  const post = useMemo(
    () => (posts || []).find((p) => p.slug === slug && p.isPublished),
    [posts, slug]
  )

  const related = useMemo(() => {
    if (!post) return []
    return (posts || [])
      .filter((p) => p.isPublished && p.id !== post.id && p.category === post.category)
      .slice(0, 3)
  }, [posts, post])

  useEffect(() => {
    if (post?.title) {
      const original = document.title
      document.title = `${post.title} | DLBC Clarksville Blog`
      return () => {
        document.title = original
      }
    }
  }, [post?.title])

  const handleShare = async () => {
    if (navigator.share && post) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        })
      } catch {
        /* user cancelled */
      }
    } else {
      navigator.clipboard?.writeText(window.location.href)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!post) {
    return (
      <section className="py-32 text-center max-w-2xl mx-auto px-4">
        <Newspaper className="w-14 h-14 mx-auto mb-4 text-gray-300" />
        <h1 className="text-3xl font-heading font-bold text-dark-100 mb-3">
          Post not found
        </h1>
        <p className="text-dark-300 mb-8">
          The post you're looking for doesn't exist or hasn't been published.
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-600 text-white font-bold"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Blog
        </Link>
      </section>
    )
  }

  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden bg-gradient-to-br from-dark-100 to-dark">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-secondary-500/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {post.category && (
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-500/20 text-accent-300 text-sm font-bold mb-5 border border-accent-400/30">
                {categoryLabels[post.category] || post.category}
              </span>
            )}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-white/70">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {post.publishedAt
                  ? formatDate(post.publishedAt?.toDate?.() || post.publishedAt, 'MMMM d, yyyy')
                  : ''}
              </span>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cover */}
      {post.coverImageUrl && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 md:-mt-10 relative z-10">
          <motion.img
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            src={post.coverImageUrl}
            alt={post.title}
            className="w-full max-h-[500px] object-cover rounded-3xl shadow-2xl"
          />
        </div>
      )}

      {/* Body */}
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.excerpt && (
            <p className="text-xl text-dark-300 italic mb-10 leading-relaxed">
              {post.excerpt}
            </p>
          )}

          <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-dark-100 prose-a:text-primary-600 prose-strong:text-dark-100 prose-img:rounded-2xl">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.body}
            </ReactMarkdown>
          </div>

          {post.tags?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-light-200 flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-dark-300" />
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-light-100 text-dark-300 text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-light-100 border-t border-light-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-heading font-bold text-dark-100 mb-8 text-center">
              More from the Blog
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-soft border border-light-200 hover:shadow-lg transition-all h-full">
                    <div className="h-40 bg-gradient-to-br from-primary-100 to-secondary-100 relative overflow-hidden">
                      {p.coverImageUrl ? (
                        <img
                          src={p.coverImageUrl}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Newspaper className="w-10 h-10 text-primary-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-dark-100 mb-2 group-hover:text-primary-600 transition-colors">
                        {p.title}
                      </h3>
                      <div className="inline-flex items-center gap-1 text-primary-600 font-semibold text-sm">
                        Read More <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default BlogPost
